import React, { useState } from 'react';
import { 
  FolderPlus, 
  Edit2, 
  Trash2, 
  Check, 
  X, 
  Layers, 
  Tv, 
  Film, 
  Upload,
  Calendar,
  ExternalLink,
  Sparkles
} from 'lucide-react';
import { CuratedCollection, MediaItem, UserProfile } from '../../types';
import { deleteUserCollection, saveCollection, cleanupDuplicateCollectionsAndCatalog } from '../../lib/firebase';

interface CollectionManagerTabProps {
  collections: CuratedCollection[];
  catalog: MediaItem[];
  currentUser?: UserProfile | null;
  onOpenImport?: () => void;
}

export const CollectionManagerTab: React.FC<CollectionManagerTabProps> = ({
  collections,
  catalog,
  currentUser,
  onOpenImport
}) => {
  const [editingCollection, setEditingCollection] = useState<CuratedCollection | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeduplicating, setIsDeduplicating] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const userId = currentUser?.uid || '';

  const handleDeduplicate = async () => {
    if (!userId) return;
    setIsDeduplicating(true);
    setFeedbackMessage(null);
    try {
      const res = await cleanupDuplicateCollectionsAndCatalog(userId);
      setFeedbackMessage({
        type: 'success',
        text: res.deletedCollections > 0 || res.deletedItems > 0
          ? `S'han eliminat ${res.deletedCollections} col·leccions duplicades i ${res.deletedItems} títols redundants.`
          : "Tot al dia: No s'han trobat col·leccions ni títols duplicats."
      });
      setTimeout(() => setFeedbackMessage(null), 4000);
    } catch (err: any) {
      setFeedbackMessage({ type: 'error', text: `Error en deduplicar: ${err.message}` });
    } finally {
      setIsDeduplicating(false);
    }
  };

  const handleStartEdit = (col: CuratedCollection) => {
    setEditingCollection({ ...col });
    setFeedbackMessage(null);
  };

  const handleDelete = async (colId: string) => {
    if (window.confirm("Segur que vols eliminar aquesta col·lecció i els seus títols associats?")) {
      try {
        await deleteUserCollection(userId, colId);
        setFeedbackMessage({ type: 'success', text: "Col·lecció eliminada correctament." });
        setTimeout(() => setFeedbackMessage(null), 3000);
      } catch (err: any) {
        setFeedbackMessage({ type: 'error', text: `Error: ${err.message}` });
      }
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCollection) return;
    if (!editingCollection.title.trim()) {
      alert("Si us plau, introdueix un títol per a la col·lecció.");
      return;
    }

    setIsSaving(true);
    try {
      const slug = editingCollection.slug.trim() || 
        editingCollection.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      
      const payload: CuratedCollection = {
        ...editingCollection,
        userId: userId || 'guest',
        title: editingCollection.title.trim(),
        slug,
        description: editingCollection.description?.trim() || '',
        order: Number(editingCollection.order) || 1,
        updatedAt: new Date().toISOString()
      };

      await saveCollection(payload);
      setEditingCollection(null);
      setFeedbackMessage({ type: 'success', text: "Col·lecció actualitzada correctament!" });
      setTimeout(() => setFeedbackMessage(null), 3500);
    } catch (err: any) {
      setFeedbackMessage({ type: 'error', text: `Error al guardar: ${err.message}` });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-display font-bold text-lg sm:text-xl text-white flex items-center gap-2">
              <Layers className="w-5 h-5 text-sky-400" />
              Gestió de les Teves Col·leccions
            </h3>
            <span className="px-2 py-0.5 rounded-full text-xs font-mono bg-sky-500/20 text-sky-300 border border-sky-500/30">
              {collections.length} col·leccions
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Cada fitxer JSON que puges crea una col·lecció independent amb les seves sèries i pel·lícules.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            id="btn-deduplicate-collections"
            type="button"
            onClick={handleDeduplicate}
            disabled={isDeduplicating}
            className="px-3.5 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-slate-200 text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50 border border-white/10"
            title="Deduplica col·leccions i títols repetits mantenint només una instància canònica"
          >
            <Sparkles className={`w-4 h-4 text-amber-400 ${isDeduplicating ? 'animate-spin' : ''}`} />
            <span>{isDeduplicating ? 'Deduplicant...' : 'Netejar Duplicats'}</span>
          </button>

          {onOpenImport && (
            <button
              id="btn-upload-new-collection"
              onClick={onOpenImport}
              className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white text-xs font-bold flex items-center gap-2 shadow-lg shadow-sky-500/20 transition-all"
            >
              <Upload className="w-4 h-4" />
              <span>Pujar Nova Col·lecció (JSON)</span>
            </button>
          )}
        </div>
      </div>

      {feedbackMessage && (
        <div className={`p-4 rounded-xl text-xs flex items-center gap-2 border animate-in fade-in ${
          feedbackMessage.type === 'success' 
            ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' 
            : 'bg-red-500/15 border-red-500/40 text-red-300'
        }`}>
          {feedbackMessage.type === 'success' ? <Check className="w-4 h-4 shrink-0" /> : <X className="w-4 h-4 shrink-0" />}
          <span>{feedbackMessage.text}</span>
        </div>
      )}

      {/* Edit Collection Modal / Section */}
      {editingCollection && (
        <div className="p-5 rounded-2xl bg-[#0f1629] border-2 border-sky-500/50 space-y-4 animate-in fade-in">
          <div className="flex items-center justify-between pb-3 border-b border-white/10">
            <h4 className="font-bold text-sm text-white flex items-center gap-2">
              <Edit2 className="w-4 h-4 text-sky-400" />
              Editar Dades de la Col·lecció: {editingCollection.title}
            </h4>
            <button
              onClick={() => setEditingCollection(null)}
              className="p-1 rounded-lg text-slate-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-300">Títol de la Col·lecció</label>
                <input
                  type="text"
                  value={editingCollection.title}
                  onChange={(e) => setEditingCollection({ ...editingCollection, title: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-white/10 text-white text-xs focus:border-sky-500 focus:outline-none"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-300">Identificador / Slug URL</label>
                <input
                  type="text"
                  value={editingCollection.slug}
                  onChange={(e) => setEditingCollection({ ...editingCollection, slug: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-white/10 text-white text-xs font-mono focus:border-sky-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-300">Descripció</label>
              <textarea
                value={editingCollection.description || ''}
                onChange={(e) => setEditingCollection({ ...editingCollection, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-white/10 text-white text-xs focus:border-sky-500 focus:outline-none"
                placeholder="Explica breument de què tracta aquesta col·lecció..."
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setEditingCollection(null)}
                className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 text-xs font-semibold"
              >
                Cancel·lar
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-5 py-2 rounded-xl bg-sky-500 hover:bg-sky-400 text-black text-xs font-bold flex items-center gap-1.5"
              >
                {isSaving ? 'Guardant...' : 'Desar Canvis'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Existing Collections Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {collections.map((col) => {
          const associatedItems = catalog.filter(i => i.collectionId === col.id || col.itemIds?.includes(i.id));
          const seriesInCol = associatedItems.filter(i => i.type === 'series');
          const moviesInCol = associatedItems.filter(i => i.type === 'movie');

          return (
            <div
              key={col.id}
              className="p-5 rounded-2xl bg-[#0d1424] border border-white/10 hover:border-sky-500/40 transition-all space-y-4 shadow-md"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono font-bold text-sky-400 bg-sky-500/10 px-2 py-0.5 rounded border border-sky-500/20">
                      {col.sourceType === 'url_sync' ? 'Sincronitzat URL' : 'Pujada JSON'}
                    </span>
                    <span className="text-[10px] font-mono text-slate-400 truncate">
                      {col.slug}
                    </span>
                  </div>
                  <h4 className="font-display font-bold text-base text-white truncate">
                    {col.title}
                  </h4>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => handleStartEdit(col)}
                    className="p-2 rounded-xl bg-white/5 hover:bg-sky-500 hover:text-black text-slate-300 transition-colors"
                    title="Editar dades"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(col.id)}
                    className="p-2 rounded-xl bg-white/5 hover:bg-red-500 hover:text-white text-slate-400 transition-colors"
                    title="Eliminar col·lecció"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <p className="text-xs text-slate-300 line-clamp-2 leading-relaxed">
                {col.description || "Sense descripció."}
              </p>

              {/* Sample Posters inside Collection */}
              <div className="flex items-center gap-1.5 overflow-hidden py-1">
                {associatedItems.slice(0, 6).map(item => (
                  <img
                    key={item.id}
                    src={item.poster}
                    alt={item.title}
                    className="w-10 h-14 object-cover rounded-md border border-white/10 shrink-0"
                    referrerPolicy="no-referrer"
                    title={item.title}
                  />
                ))}
                {associatedItems.length > 6 && (
                  <div className="w-10 h-14 rounded-md bg-slate-800 border border-white/10 flex items-center justify-center text-[10px] font-bold text-slate-300 shrink-0">
                    +{associatedItems.length - 6}
                  </div>
                )}
              </div>

              <div className="pt-3 border-t border-white/10 flex items-center justify-between text-xs text-slate-400">
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1 text-sky-300 font-semibold">
                    <Tv className="w-3.5 h-3.5" />
                    <span>{col.seriesCount || seriesInCol.length} sèries</span>
                  </span>
                  <span className="flex items-center gap-1 text-cyan-300 font-semibold">
                    <Film className="w-3.5 h-3.5" />
                    <span>{col.moviesCount || moviesInCol.length} pel·lis</span>
                  </span>
                </div>
                
                <span className="text-[11px] text-slate-400">
                  {col.totalItems || associatedItems.length} títols totals
                </span>
              </div>
            </div>
          );
        })}

        {collections.length === 0 && (
          <div className="col-span-2 text-center py-12 px-4 rounded-2xl bg-[#0d1424] border border-dashed border-white/15">
            <Layers className="w-12 h-12 text-sky-400 mx-auto mb-3 opacity-60" />
            <h4 className="text-base font-bold text-white mb-1">Encara no tens cap col·lecció</h4>
            <p className="text-xs text-slate-400 max-w-md mx-auto mb-4">
              Cada col·lecció es crea a partir d'un fitxer JSON amb sèries o pel·lícules. Puja el teu primer fitxer per començar.
            </p>
            {onOpenImport && (
              <button
                onClick={onOpenImport}
                className="px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-400 text-black text-xs font-bold inline-flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                <span>Pujar Fitxer JSON</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
